// Infinite-scroll projects data layer for the dashboard table.
//
// The main list is paginated 100/page (plain-array response; total from
// row[0].count). We accumulate pages locally keyed by the current filter
// signature, append on `nextPage`, and stop when a page returns < 100 rows
// (§3.E / §4 / parity 98-103). Pinned projects use the triple-n `pinnned=true`
// param and are NOT paginated.

import { useEffect, useMemo, useRef, useState } from 'react';
import { dashboardApi } from 'app/services/dashboardApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setHasMore } from 'app/slices/dashboardSlice';
import type { FilterMap, IDashboardProject } from 'models';

const PAGE_SIZE = 100;

/** Stable empty list so gated renders don't churn effect dependencies. */
const NO_ROWS: IDashboardProject[] = [];

const { useGetProjectsQuery, useGetPinnedProjectsQuery } = dashboardApi;

/** Combine the column-search filters with the status-rail task filter (§3.C). */
export function useEffectiveFilters(): FilterMap {
  const filters = useAppSelector((s) => s.dashboard.filters);
  const taskFilter = useAppSelector((s) => s.dashboard.taskFilter);
  return useMemo(() => {
    const out: FilterMap = { ...filters };
    if (taskFilter) out.task = taskFilter;
    else delete out.task;
    return out;
  }, [filters, taskFilter]);
}

/** Stable signature for a filter map (order-independent). */
function filterSig(filters: FilterMap): string {
  return Object.keys(filters)
    .sort()
    .map((k) => `${k}=${filters[k]}`)
    .join('&');
}

/** Whether ANY filter field has a non-empty value (Filtered "active" gate). */
export function isFilterActive(filters: FilterMap): boolean {
  return Object.values(filters).some((v) => v != null && String(v).trim() !== '');
}

export interface DashboardProjectsResult {
  /** Accumulated main (un-pinned) rows across loaded pages. */
  rows: IDashboardProject[];
  /** Pinned rows (single fetch, triple-n). */
  pinnedRows: IDashboardProject[];
  /** Server grand total (state-independent `total_count`); sticky when lists are empty. */
  total: number;
  /** Whether more pages remain to load. */
  hasMore: boolean;
  /** True while the first page (or pinned) is loading. */
  isLoading: boolean;
  /** True while a subsequent page is fetching. */
  isFetchingMore: boolean;
  /** True while EITHER list query is (re)fetching — incl. invalidation refetches. */
  isFetching: boolean;
  /** True once BOTH lists have settled for the current filter signature. */
  listsReady: boolean;
}

/**
 * Main + pinned project lists with page accumulation. Filters (column searches +
 * task) drive both list URLs. Changing filters resets accumulation to page 1.
 */
export function useDashboardProjects(): DashboardProjectsResult {
  const dispatch = useAppDispatch();
  const filters = useEffectiveFilters();
  const page = useAppSelector((s) => s.dashboard.page);
  const hasMore = useAppSelector((s) => s.dashboard.hasMore);
  // Active/Archived/All toggle (dev delta): always sent as `project_state` on
  // BOTH lists, but kept OUT of `filters` so it never trips the Filtered-count
  // or .no-drag gates (it's a view mode, not a user filter). Included in the
  // accumulation signature so toggling resets to page 1 cleanly.
  const projectState = useAppSelector((s) => s.dashboard.projectState);
  const queryFilters = useMemo(
    () => ({ ...filters, project_state: projectState }),
    [filters, projectState]
  );

  const sig = filterSig(queryFilters);

  // Accumulated rows keyed by filter signature. When the signature changes we
  // discard the accumulation and rebuild from page 1.
  const [accumulated, setAccumulated] = useState<IDashboardProject[]>([]);
  const accSigRef = useRef<string>(sig);
  const lastPageRef = useRef<number>(0);
  // Last server total_count seen (sticky, like the Backbone collection property).
  const cachedTotalRef = useRef<number>(0);

  // refetchOnMountOrArgChange: legacy has NO client cache — every filter/state/
  // page transition refetches the collections (with the whiteboard preloader,
  // which ProjectTable already ties to isFetching). Serving a cached RTK entry
  // here breaks the drag-reorder edge: a pure drag persists custom_order WITHOUT
  // invalidating the lists (QA #4 — no refetch, no spinner), so an entry cached
  // BEFORE the drag holds the pre-drag order; apply+clear a filter within the
  // cache window and the stale entry would snap the rows back. Always refetching
  // on arg change restores legacy semantics (drag itself changes no args, so it
  // still triggers no fetch and no spinner).
  const mainQuery = useGetProjectsQuery(
    { page, filters: queryFilters },
    { refetchOnMountOrArgChange: true }
  );
  const pinnedQuery = useGetPinnedProjectsQuery(
    { filters: queryFilters },
    { refetchOnMountOrArgChange: true }
  );

  const mainPage = mainQuery.data;
  const pinnedPage = pinnedQuery.data;

  // Reset accumulation when the filter signature changes.
  useEffect(() => {
    if (accSigRef.current !== sig) {
      accSigRef.current = sig;
      lastPageRef.current = 0;
      setAccumulated([]);
    }
  }, [sig]);

  // ---- Render gate: both lists, or neither ----
  // Legacy draws the pinned and default lists in ONE pass, only after BOTH
  // fetches have resolved: whiteboard/mvc/view/projects.js:131-174 wraps
  // `collection.fetch()` + `pined_collection.fetch()` in a Promise.all and calls
  // renderProjects() in its .then; the filter path (:395-403) chains
  // pinned → main and renders off the main collection's `reset`. Either way a
  // single render emits both lists.
  //
  // RTK resolves the two queries independently, so whichever returned first
  // painted on its own — on a cold dashboard the pinned rows (or the main list)
  // appeared alone next to a still-spinning preloader, then the other list
  // popped in underneath. Hold both back until the PAIR has settled for the
  // current filter signature.
  //
  // Keyed by signature rather than latched once, so a filter/state change gates
  // again (both queries restart there). Page N>1 keeps the same signature, so
  // infinite scroll still appends without blanking the list, and an
  // invalidation refetch (pin, duplicate, delete) leaves the rows on screen
  // while it runs — both as before.
  const mainSettled = (mainQuery.isSuccess || mainQuery.isError) && !mainQuery.isFetching;
  const pinnedSettled = (pinnedQuery.isSuccess || pinnedQuery.isError) && !pinnedQuery.isFetching;
  const [readySig, setReadySig] = useState<string | null>(null);
  useEffect(() => {
    if (mainSettled && pinnedSettled) setReadySig(sig);
  }, [mainSettled, pinnedSettled, sig]);
  const listsReady = readySig === sig;

  // Merge each successfully-fetched page into the accumulation.
  //
  // IMPORTANT: key the merge off the page the resolved data ACTUALLY belongs to
  // (`mainQuery.originalArgs.page`), NOT the current `page` state, and only act
  // on a SETTLED query (`isSuccess && !isFetching`). During the page→N+1
  // transition the hook keeps returning the previous page's `data`; using the
  // current `page` there would advance `lastPageRef` against stale rows and
  // permanently block the real page-(N+1) append (infinite scroll stuck at 100).
  const fetchedPage = (mainQuery.originalArgs as { page?: number } | undefined)?.page ?? 1;
  const settled = mainQuery.isSuccess && !mainQuery.isFetching;
  useEffect(() => {
    if (!mainPage || !settled) return;
    // Page 1 (or after a reset) replaces; later pages append.
    if (fetchedPage === 1 || lastPageRef.current === 0) {
      setAccumulated(mainPage.rows);
    } else if (fetchedPage > lastPageRef.current) {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const fresh = mainPage.rows.filter((p) => !seen.has(p.id));
        return [...prev, ...fresh];
      });
    } else {
      // Already merged this page (or older) — do not advance the ref.
      return;
    }
    lastPageRef.current = fetchedPage;

    // hasMore = the just-fetched page was full (parity 99).
    const full = mainPage.rows.length >= PAGE_SIZE;
    if (full !== hasMore) dispatch(setHasMore(full));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainPage, fetchedPage, settled]);

  // Server total (QA #3): legacy infobar.js:59-64 reads the STATE-INDEPENDENT
  // `total_count` field carried on every row of BOTH lists (grand total across
  // all project states, pinned + unpinned, ignoring search filters —
  // rest_project/viewset.py:113-116) and keeps the last seen value on the
  // collection. Mirror that stickiness: when both lists come back empty (e.g.
  // a no-match filter) the previous total keeps showing.
  const total = useMemo(() => {
    const t = mainPage?.rows[0]?.total_count ?? pinnedPage?.rows[0]?.total_count;
    if (t != null) cachedTotalRef.current = t;
    return cachedTotalRef.current;
  }, [mainPage, pinnedPage]);

  return {
    rows: listsReady ? accumulated : NO_ROWS,
    pinnedRows: listsReady ? pinnedPage?.rows ?? NO_ROWS : NO_ROWS,
    total,
    hasMore,
    isLoading: (mainQuery.isLoading && page === 1) || pinnedQuery.isLoading,
    isFetchingMore: mainQuery.isFetching && page > 1,
    isFetching: mainQuery.isFetching || pinnedQuery.isFetching,
    listsReady,
  };
}
